import {
  DeviceWithSeries,
  SeriesToShowByCategory,
  TrendingCategory,
  TrendingCategoryItem,
} from './../trending-chart.helpers';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { FeatureNames } from '../../../../../../projects/client';
import { InstallationFeaturesStore } from '../../../../../@store';
import { TrendingSeries } from '../trending-chart.helpers';
import { constantWord } from 'src/app/shared/common/enums';

@Component({
  selector: 'app-trending-chart-legend',
  templateUrl: './trending-chart-legend.component.html',
  styleUrls: ['./trending-chart-legend.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrendingChartLegendComponent {
  @Input() chart: Highcharts.Chart;
  @Input() trendingCategories: TrendingCategory[];

  FeatureNames = FeatureNames;
  total = constantWord.Total;

  protected devicesWithSeries: DeviceWithSeries[];
  private devicesWithAllSeries: DeviceWithSeries[];
  constructor(
    protected installationFeaturesStore: InstallationFeaturesStore,
    private cdr: ChangeDetectorRef
  ) {}

  redrawLegend(): void {
    const uniqueDevices = [];
    const _devices: DeviceWithSeries[] = [];

    const series: TrendingSeries[] = (<unknown>this.chart.series) as TrendingSeries[];

    series.forEach((entry) => {
      if (!uniqueDevices.includes(entry.userOptions.boilerSerial)) {
        uniqueDevices.push(entry.userOptions.boilerSerial);
      }
    });

    uniqueDevices.forEach((boilerSerial) => {
      const deviceSeries = series.filter((series) => {
        return (
          !series.name.includes('Navigator') && series.userOptions.boilerSerial === boilerSerial
        );
      });

      if (
        deviceSeries.some(
          (series) => series.userOptions.name?.toLowerCase() === this.total.toLowerCase()
        )
      ) {
        const seriesTotal = deviceSeries.find(
          (series) => series.userOptions.name?.toLowerCase() === this.total.toLowerCase()
        );
        const indexOfTotal = deviceSeries.indexOf(seriesTotal);
        deviceSeries.unshift(...deviceSeries.splice(indexOfTotal, 1));
      }

      const firstSeries = deviceSeries[0];
      const currentDevice: DeviceWithSeries = {
        deviceId: firstSeries.userOptions.deviceId,
        deviceType: firstSeries.userOptions.deviceType,
        boilerSerial: firstSeries.userOptions.boilerSerial,
        shortDeviceName: firstSeries.userOptions.shortDeviceName,
        data: deviceSeries,
      };
      firstSeries.userOptions.deviceId === this.total
        ? _devices.unshift(currentDevice)
        : _devices.push(currentDevice);
    });
    this.devicesWithAllSeries = _devices;
    this.devicesWithSeries =
      this.trendingCategories && this.trendingCategories.length > 0
        ? this.getDeviceSeriesByModule(this.devicesWithAllSeries)
        : this.devicesWithAllSeries;
    this.cdr.markForCheck();
  }

  isTooltipDisabled(element: HTMLElement): boolean {
    return !!element.scrollWidth ? element.scrollWidth <= element.clientWidth : false;
  }

  onItemLegendClick(series: TrendingSeries): void {
    series.eventOptions.legendItemClick({ target: series });
  }

  getCategoryNameListByDeviceId(deviceId: string): TrendingCategoryItem[] {
    if (!this.trendingCategories) {
      return [];
    }
    const selectedTrendingCategory = this.trendingCategories.find(
      (item) => item.deviceId === deviceId
    );

    return selectedTrendingCategory.items;
  }

  onSelectCategory(deviceId: string, trendingCategoryItem: TrendingCategoryItem): void {
    const numberOfActiveItem: number = this.numberOfActiveCategory(this.trendingCategories);
    const selectedSeriesData: TrendingSeries[] = this.getSeriesToShow(
      deviceId,
      trendingCategoryItem.features,
      this.devicesWithAllSeries
    );

    if (numberOfActiveItem === 1 && trendingCategoryItem.active) {
      return;
    }

    this.trendingCategories.forEach((trendingCategory) => {
      if (trendingCategory.deviceId === deviceId) {
        trendingCategory.items.forEach((item) => {
          item.active =
            item.categoryId === trendingCategoryItem.categoryId ? !item.active : item.active;
        });
      }
    });

    this.onAutoSelectLegend(trendingCategoryItem.active, selectedSeriesData);
    this.redrawLegend();
  }

  private getSeriesToShow(
    deviceId: string,
    features: string[],
    devicesWithSeries: DeviceWithSeries[]
  ): TrendingSeries[] {
    const selectedDevice = devicesWithSeries.find((device) => device.deviceId === deviceId);
    if (features && selectedDevice) {
      return selectedDevice.data.filter((series) =>
        features.some((feature) => series.userOptions.id.includes(feature))
      );
    }

    return [];
  }

  private getDeviceSeriesByModule(devicesWithSeries: DeviceWithSeries[] = []): DeviceWithSeries[] {
    const seriesToShowByCategory: SeriesToShowByCategory[] = [];
    if (!this.trendingCategories || this.trendingCategories.length === 0) {
      return devicesWithSeries;
    }

    this.trendingCategories.forEach((item) => {
      const selectedTrendingCategoryItems: string[] = [];
      this.getCategoryNameListByDeviceId(item.deviceId).forEach((category) => {
        if (category.active) {
          selectedTrendingCategoryItems.push(...category.features);
        }
      });
      seriesToShowByCategory.push({
        deviceId: item.deviceId,
        categoryId: null,
        series: this.getSeriesToShow(
          item.deviceId,
          selectedTrendingCategoryItems,
          devicesWithSeries
        ),
      });
    });

    return devicesWithSeries.map((device) => {
      return {
        ...device,
        data: seriesToShowByCategory.find((series) => series.deviceId === device.deviceId).series,
      };
    });
  }

  private numberOfActiveCategory(trendingCategories: TrendingCategory[]): number {
    if (!trendingCategories || trendingCategories.length === 0) {
      return null;
    }

    return trendingCategories.reduce((total, trendingCategory) => {
      const activeCount = trendingCategory.items.filter((item) => item.active).length;

      return total + activeCount;
    }, 0);
  }

  private onAutoSelectLegend(status: boolean, trendingSeries: TrendingSeries[]): void {
    if (status) {
      trendingSeries.forEach((series) => this.onItemLegendClick(series));
    } else {
      trendingSeries.forEach((series) => {
        if (series.visible) {
          this.onItemLegendClick(series);
        }
      });
    }
  }
}
